import { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { has, isArray } from "lodash";

import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { socketConnection } from "../../services/socket";
// import { useDate } from "../../hooks/useDate";
import moment from "moment";

const useAuth = () => {
  const history = useHistory();
  const [isAuth, setIsAuth] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState({});
  const [socket, setSocket] = useState({})
  const requestInterceptorRef = useRef(null);
  const responseInterceptorRef = useRef(null);

  useEffect(() => {
    requestInterceptorRef.current = api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem("token");
        if (token) {
          config.headers["Authorization"] = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    responseInterceptorRef.current = api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error?.config || {};
        const requestUrl = String(originalRequest?.url || "");

        if (
          error?.response?.status === 403 &&
          !originalRequest._retry &&
          !requestUrl.includes("/auth/refresh_token")
        ) {
          originalRequest._retry = true;
          try {
            const { data } = await api.post("/auth/refresh_token");
            if (data?.token) {
              localStorage.setItem("token", data.token);
              api.defaults.headers.Authorization = `Bearer ${data.token}`;
              setIsAuth(true);
              originalRequest.headers = originalRequest.headers || {};
              originalRequest.headers.Authorization = `Bearer ${data.token}`;
              return api(originalRequest);
            }
          } catch (_) {
            // Keep original rejection path below.
          }
        }

        if (error?.response?.status === 401) {
          localStorage.removeItem("token");
          api.defaults.headers.Authorization = undefined;
          setIsAuth(false);
        }
        return Promise.reject(error);
      }
    );

    return () => {
      if (requestInterceptorRef.current !== null) {
        api.interceptors.request.eject(requestInterceptorRef.current);
      }
      if (responseInterceptorRef.current !== null) {
        api.interceptors.response.eject(responseInterceptorRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    (async () => {
      if (token) {
        try {
          const { data } = await api.post("/auth/refresh_token");
          api.defaults.headers.Authorization = `Bearer ${data.token}`;
          setIsAuth(true);
          setUser(data.user);
          if (data?.user?.language) {
            i18n.changeLanguage(data.user.language).catch(() => undefined);
          }
        } catch (err) {
          toastError(err);
        }
      }
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (user && typeof user === "object" && Object.keys(user).length && user.id > 0) {
      // console.log("Entrou useWhatsapp com user", Object.keys(user).length, Object.keys(socket).length ,user, socket)
      const io = socketConnection({ user });
      if (io && io !== socket) setSocket(io);
      io.on(`company-${user.companyId}-user`, (data) => {
        if (data.action === "update" && data.user.id === user.id) {
          setUser(data.user);
        }
      });

      return () => {
        // console.log("desconectou o company user ", user.id)
        io.off(`company-${user.companyId}-user`);
        // io.disconnect();
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }
  }, [user]);

  const handleLogin = async (userData) => {
    setLoading(true);

    try {
      const { data } = await api.post("/auth/login", userData);
      const {
        user: { company },
      } = data;

      if (has(company, "companieSettings") && isArray(company.companieSettings[0])) {
        const setting = company.companieSettings[0].find(
          (s) => s.key === "campaignsEnabled"
        );
        if (setting && setting.value === "true") {
          localStorage.setItem("cshow", null); //regra pra exibir campanhas
        }
      }

      if (has(company, "companieSettings") && isArray(company.companieSettings[0])) {
        const setting = company.companieSettings[0].find(
          (s) => s.key === "sendSignMessage"
        );

        const signEnable = setting.value === "enable";

        if (setting && setting.value === "enabled") {
          localStorage.setItem("sendSignMessage", signEnable); //regra pra exibir campanhas
        }
      }
      localStorage.setItem("profileImage", data.user.profileImage); //regra pra exibir imagem contato

      moment.locale('pt-br');
      const rawDueDate = data.user.company?.dueDate;
      const dueDate = data.user.company?.id === 1 ? null : rawDueDate;

      const hasValidDueDate = !!dueDate && moment(dueDate).isValid();
      const vencimento = hasValidDueDate ? moment(dueDate).format("DD/MM/YYYY") : "";

      const before = !hasValidDueDate ? true : moment().isBefore(dueDate);
      const dias = !hasValidDueDate ? null : moment.duration(moment(dueDate).diff(moment())).asDays();

      if (before === true) {
        localStorage.setItem("token", data.token);
        localStorage.setItem("user", JSON.stringify(data.user));
        // localStorage.setItem("public-token", JSON.stringify(data.user.token));
        // localStorage.setItem("companyId", companyId);
        // localStorage.setItem("userId", id);
        if (vencimento) {
          localStorage.setItem("companyDueDate", vencimento);
        } else {
          localStorage.removeItem("companyDueDate");
        }
        api.defaults.headers.Authorization = `Bearer ${data.token}`;
        setUser(data.user);
        setIsAuth(true);
        if (data?.user?.language) {
          try {
            await i18n.changeLanguage(data.user.language);
          } catch (_) { }
        }
        toast.success(i18n.t("auth.toasts.success"));
        if (dias !== null && Math.round(dias) < 5) {
          const rounded = Math.max(0, Math.round(dias));
          if (rounded === 0) {
            toast.warn(i18n.t("auth.dueDate.expirationToday"));
          } else {
            toast.warn(`${i18n.t("auth.dueDate.expiration")} ${rounded} ${rounded === 1 ? i18n.t("auth.dueDate.day") : i18n.t("auth.dueDate.days")}`);
          }
        }

        // // Atraso para garantir que o cache foi limpo
        // setTimeout(() => {
        //   window.location.reload(true); // Recarregar a página
        // }, 1000);

        history.push("/dashboard");
        setLoading(false);
        return { ok: true };
      } else {
        // localStorage.setItem("companyId", companyId);
        api.defaults.headers.Authorization = `Bearer ${data.token}`;
        setIsAuth(true);
        toastError(`Tu suscripción venció el ${vencimento}. Contacta a soporte para más información.`);
        history.push("/financeiro-aberto");
        setLoading(false);
        return { ok: false, error: `Tu suscripción venció el ${vencimento}.` };
      }

    } catch (err) {
      toastError(err);
      setLoading(false);
      const message =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.response?.data?.error;
      return { ok: false, error: message };
    }
  };

  const handleLogout = async () => {
    setLoading(true);

    try {
      try {
        if (socket && typeof socket === "object" && typeof socket.disconnect === "function") {
          socket.disconnect();
        }
      } catch (_) { }
      setSocket({});
      await api.delete("/auth/logout");
      setIsAuth(false);
      setUser({});
      localStorage.removeItem("token");
      localStorage.removeItem("cshow");
      // localStorage.removeItem("public-token");
      api.defaults.headers.Authorization = undefined;
      setLoading(false);
      history.push("/login");
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const getCurrentUserInfo = async () => {
    try {
      const { data } = await api.get("/auth/me");
      return data;
    } catch (_) {
      return null;
    }
  };

  return {
    isAuth,
    user,
    loading,
    handleLogin,
    handleLogout,
    getCurrentUserInfo,
    socket
  };
};

export default useAuth;
